import React, { useState, useCallback, useMemo, useEffect } from "react";
import _ from "lodash";
import { Trans, useTranslation } from "react-i18next";

import * as SC from "./CreditModal.styled";

import { Store__System, Button as BtnPrimary } from "@talentbait/shared-components";
import Actions from "../../Actions";
import logger from "../../utils/LoggerFrontend";
import { formatIntlNumber, numberFormat } from "../../utils/Helpers";
import { CampaignCreationStore, withRefluxStore } from "../../Stores";
import InvoiceAddressSelector from "../SelfServiceCampaignCreation/Campaign__Create_InvoiceAddressSelector.jsx";
import CreateInvoiceAddressModal from "../SelfServiceCampaignCreation/campaign-creation/invoice-address-modal/CreateInvoiceAddressModal.jsx";

import ReviewStep from "./ReviewStep";
import FinishedView from "./FinishedView";
import LoadingView from "./LoadingView";

const CreditModal = ({ organization, userProfile, invoiceAddresses: orgaInvoiceAddresses }) => {
  const { t } = useTranslation();

  const [selectedPrepaidPackageId, setSelectedPrepaidPackageId] = useState("");
  const [prepaidPackages, setPrepaidPackages] = useState([]);
  const [selectedInvoiceAddress, setSelectedInvoiceAddress] = useState(null);
  const [invoiceAddresses, setInvoiceAddresses] = useState(orgaInvoiceAddresses);
  const [selectedInvoiceEmail, setSelectedInvoiceEmail] = useState("");
  const [selectedInvoiceAddressData, setSelectedInvoiceAddressData] = useState({});
  const [poNumber, setPONumber] = useState("");
  const [step, setStep] = useState(1);
  const [showInvoiceAddressCreator, setShowInvoiceAddressCreator] = useState(false);
  const [isUploadingPrepaidMoney, setIsUploadingPrepaidMoney] = useState(false);
  const [bookedInvoiceId, setBookedInvoiceId] = useState(null);
  logger.log("CreditModal:render", "called", {
    step,
    selectedPrepaidPackageId,
    organization,
    userProfile,
    invoiceAddresses,
    orgaInvoiceAddresses
  });

  useEffect(() => {
    // Fetch all available packages that can be bought
    const ref = Actions.getPrepaidPackages.completed.listen((result) => {
      logger.log("CreditModal:getPrepaidPackages", "fetched packages", { ...result });
      setPrepaidPackages(_.orderBy(result?.payload?.prepaidPackages || [], "prepaidMoney", "asc"));
    });

    const refFailed = Actions.getPrepaidPackages.failed.listen((error) => {
      logger.log("error:CreditModal:getPrepaidPackages", "Unable to fetch packages", { error });
    });

    Actions.getPrepaidPackages();

    // Get all invoice addresses for this organization so that the user can buy a packages
    // with the given invoice address
    const refInvoiceAddresses = Actions.getCustomerAddresses.completed.listen((result) => {
      logger.log("CreditModal:getCustomerAddresses", "fetched customer address", { result });
      setInvoiceAddresses(result);
      setDefaultInvoiceAddressValues(selectedInvoiceAddress || result?.[0]?.id, result);
      setShowInvoiceAddressCreator(result && result.length === 0);
    });

    const refFailedInvoiceAddresses = Actions.getCustomerAddresses.failed.listen((error) => {
      logger.log("error:CreditModal:getCustomerAddresses", "Unable to fetch customer address", {
        error
      });
    });

    Actions.getCustomerAddresses(organization?.invoicesReceivers, false);

    return () => {
      // Clear out listeners on component unmount
      logger.debug("CreditModal:unmounted", "called");
      ref();
      refFailed();
      refInvoiceAddresses();
      refFailedInvoiceAddresses();
    };
  }, [organization]);

  useEffect(() => {
    // When the client adds a new invoice address, automatically select it fo purchasing the credit packages
    const ref = Actions.createDebitoorCompany.completed.listen(({ payload: address }) => {
      logger.log("CreditModal:onCreateDebitoorCompanyCompleted", "called...", {
        address
      });
      Actions.addNotification(t("Billing address created"));
      const newInvoicesReceivers = _.uniq(
        (organization.invoicesReceivers || []).concat([address.id])
      );
      Actions.updateOrganization(organization.id, {
        invoicesReceivers: newInvoicesReceivers
      });
      Actions.loadOrganization(organization.id);
      Actions.notifiyEvent({
        eventName: "Rechnungansadressen",
        text: `🧾 Neue Rechnungsanschrift wird für *${address.name}* / ${organization.name} hinzugefügt`
      });
      setSelectedInvoiceAddress(address.id);
      setShowInvoiceAddressCreator(false);
      setInvoiceAddresses(_.uniq(invoiceAddresses.concat([address]), "id"));
    });

    return () => {
      logger.debug("CreditModal:unmounted2", "called");
      ref();
    };
  }, [organization, invoiceAddresses]);

  const uploadCredit = useCallback(() => {
    const currentlySelectedAddress = _.find(invoiceAddresses, { id: selectedInvoiceAddress });
    logger.log("CreditModal:uploadCredit", "called", {
      organizationId: organization.id,
      packageId: selectedPrepaidPackageId,
      currentlySelectedAddress,
      selectedInvoiceEmail,
      selectedInvoiceAddressData
    });
    setIsUploadingPrepaidMoney(true);

    const ref = Actions.topUpPrepaidMoney.completed.listen((data) => {
      logger.debug("CreditModal:topUpPrepaidMoney:completed", "called: ", {
        data,
        organizationId: organization.id,
        packageId: selectedPrepaidPackageId,
        currentlySelectedAddress
      });

      // TODO: update prepaid balance on frontend

      setIsUploadingPrepaidMoney(false);
      setBookedInvoiceId(data?.payload?.bookedInvoiceId);

      Actions.addNotification(
        `The prepaid money was topped up, current balance: ${formatIntlNumber(
          data.payload.updatedPrepaidBalance,
          { language: userProfile?.language }
        )}`
      );
      ref();
    });
    const refFailed = Actions.topUpPrepaidMoney.failed.listen((err) => {
      logger.log("error:CreditModal:topUpPrepaidMoney:failed", "called: ", {
        err,
        organizationId: organization.id,
        packageId: selectedPrepaidPackageId,
        currentlySelectedAddress
      });

      setIsUploadingPrepaidMoney(false);

      Actions.addNotification(t("Unable to top up prepaid money"), "error");
      refFailed();
    });

    Actions.topUpPrepaidMoney({
      organizationId: organization.id,
      packageId: selectedPrepaidPackageId,
      invoicing: {
        organizationId: currentlySelectedAddress.id || "",
        entity: currentlySelectedAddress?.name || "",
        email: selectedInvoiceEmail || "",
        street: selectedInvoiceAddressData?.street || "",
        zip: selectedInvoiceAddressData?.zip || "",
        city: selectedInvoiceAddressData?.city || "",
        countryCode: selectedInvoiceAddressData?.countryCode || "",
        countryName: selectedInvoiceAddressData?.countryName || "",
        poNumber
      }
    });
  }, [
    selectedPrepaidPackageId,
    selectedInvoiceAddress,
    organization?.id,
    selectedInvoiceEmail,
    selectedInvoiceAddressData,
    invoiceAddresses,
    poNumber,
    t,
    userProfile?.language
  ]);

  function setDefaultInvoiceAddressValues(invoiceAddressId, invoiceAddresses) {
    setSelectedInvoiceAddress(invoiceAddressId);

    const newSelection = invoiceAddresses?.find((e) => e.id === invoiceAddressId);

    if (newSelection?.emails?.length) {
      setSelectedInvoiceEmail(newSelection.emails[newSelection.emails.length - 1]?.value || "");
    } else {
      setSelectedInvoiceEmail("");
    }

    if (newSelection?.addresses?.length) {
      setSelectedInvoiceAddressData(
        newSelection.addresses[newSelection.addresses.length - 1] || {}
      );
    } else {
      setSelectedInvoiceAddressData({});
    }
  }

  const onSelectAddress = useCallback(
    (selected) => {
      if (selected.value === "new") {
        setShowInvoiceAddressCreator(true);
      } else {
        setDefaultInvoiceAddressValues(selected.value, invoiceAddresses);
      }
    },
    [invoiceAddresses]
  );

  const selectedPackageData = useMemo(
    () => _.find(prepaidPackages, { id: selectedPrepaidPackageId }),
    [selectedPrepaidPackageId, prepaidPackages]
  );

  if (isUploadingPrepaidMoney == true) {
    return <LoadingView />;
  }
  if (bookedInvoiceId && !isUploadingPrepaidMoney) {
    return <FinishedView orgaId={organization?.id} />;
  }

  return showInvoiceAddressCreator ? (
    <CreateInvoiceAddressModal
      descriptionText={t("Create a new billing address to top up credit")}
      onCancel={() => setShowInvoiceAddressCreator(false)}
      organizationId={organization?.id}
      companyName={organization.name}
    />
  ) : step == 1 ? (
    <SC.ModalContentWrapper className="fade-in">
      <SC.Header>{t("Choose a campaign credit package")}</SC.Header>
      <SC.PackagesSection>
        {prepaidPackages.map(({ name, prepaidMoney, id, discount }) => (
          <SC.SinglePackageCard
            selected={id == selectedPrepaidPackageId}
            key={id}
            onClick={() => setSelectedPrepaidPackageId(id)}
          >
            <SC.SinglePackageCardDetailSeciton>
              <SC.SinglePackageCardDetailLeftSection>
                <SC.SinglePackageCardRadio type="radio" checked={id == selectedPrepaidPackageId} />
                <SC.SinglePackageCardDetailLeftNameSection>
                  <SC.SinglePackageCardTitle>
                    <Trans>{name}</Trans>
                  </SC.SinglePackageCardTitle>
                  <SC.SinglePackageCardSavingsPercent>
                    {t("You save")}{" "}
                    {formatIntlNumber(discount, { language: userProfile?.language })}%
                  </SC.SinglePackageCardSavingsPercent>
                </SC.SinglePackageCardDetailLeftNameSection>
              </SC.SinglePackageCardDetailLeftSection>
            </SC.SinglePackageCardDetailSeciton>
            <SC.SinglePackageCardTotalSeciton>
              <SC.SinglePackageCardSubtitle>{t("Total")}</SC.SinglePackageCardSubtitle>
              <SC.SinglePackageCardAmount>
                {formatIntlNumber(prepaidMoney, { language: userProfile?.language })} €
              </SC.SinglePackageCardAmount>
            </SC.SinglePackageCardTotalSeciton>
          </SC.SinglePackageCard>
        ))}
      </SC.PackagesSection>
      <SC.PackageHint>
        {t("Please note that credit cannot be combined with discounts or vouchers.")}
      </SC.PackageHint>
      <SC.PackageHint>
        {t(
          "Your credit is valid for 9 months from the top-up date. Please use it within this period as it will expire afterwards."
        )}
      </SC.PackageHint>
      <SC.InvoiceSelectorSection>
        <strong>{t("Please select the billing address:")}</strong>
        <br />
        <br />
        <InvoiceAddressSelector
          invoiceAddresses={invoiceAddresses}
          onSelectAddress={onSelectAddress}
          currentlySelectedAddress={_.find(invoiceAddresses, { id: selectedInvoiceAddress })}
          direction="top"
          selectedInvoiceEmail={selectedInvoiceEmail}
          selectedInvoiceAddressData={selectedInvoiceAddressData}
          setSelectedInvoiceEmail={setSelectedInvoiceEmail}
          setSelectedInvoiceAddressData={setSelectedInvoiceAddressData}
        />
      </SC.InvoiceSelectorSection>

      <div
        style={{
          position: "relative",
          top: "-5px",
          display: "flex",
          flexDirection: "row-reverse",
          marginTop: "40px"
        }}
      >
        <BtnPrimary
          mt={"10px"}
          type="primary"
          onClick={() => setStep(2)}
          disabled={
            !selectedPrepaidPackageId ||
            !selectedInvoiceAddress ||
            !selectedInvoiceEmail ||
            !selectedInvoiceAddressData?.id
          }
        >
          {t("Review")}
        </BtnPrimary>
      </div>
    </SC.ModalContentWrapper>
  ) : (
    <ReviewStep
      selectedPackageData={selectedPackageData}
      selectedPrepaidPackageId={selectedPrepaidPackageId}
      goBack={() => setStep(1)}
      disabled={!selectedPrepaidPackageId || !selectedInvoiceAddress}
      uploadCredit={uploadCredit}
      currentlySelectedAddress={_.find(invoiceAddresses, { id: selectedInvoiceAddress })}
      selectedInvoiceEmail={selectedInvoiceEmail}
      selectedInvoiceAddressData={selectedInvoiceAddressData}
      poNumber={poNumber}
      setPONumber={setPONumber}
      language={userProfile?.language}
    />
  );
};

export default withRefluxStore(
  CreditModal,
  [CampaignCreationStore, Store__System],
  ["organization", "userProfile", "invoiceAddresses"]
);
